import { AllAnalyticsEvents, AnalyticsConfig } from '@/types/analytics';

declare global {
  interface Window {
    gtag: (
      command: string,
      targetId: string,
      config?: Record<string, string | number | boolean | object>
    ) => void;
    dataLayer: object[];
  }
}

export const analyticsConfig: AnalyticsConfig = {
  measurementId: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || '',
  scrollDepth: {
    thresholds: [25, 50, 75, 100],
    debounceTime: 1000,
  },
  enableDebugMode: process.env.NODE_ENV === 'development',
  enablePerformanceTracking: true,
};

export const trackEvent = (event: AllAnalyticsEvents): void => {
  if (typeof window === 'undefined' || !window.gtag) {
    if (analyticsConfig.enableDebugMode) {
      console.warn('Google Analytics not available');
    }
    return;
  }

  const { action, category, label, value, custom_parameters } = event;

  if (analyticsConfig.enableDebugMode) {
    console.log('📊 Analytics Event:', {
      event_name: action,
      event_category: category,
      event_label: label,
      value,
      ...custom_parameters,
    });
  }

  try {
    const eventParams: Record<string, string | number | boolean | object> = {
      event_category: category,
      event_label: label,
      ...custom_parameters,
    };

    if (value !== undefined) {
      eventParams.value = value;
    }

    window.gtag('event', action, eventParams);
  } catch (error) {
    if (analyticsConfig.enableDebugMode) {
      console.error('Analytics tracking error:', error);
    }
  }
};

export const trackPageView = (url: string, title?: string): void => {
  if (typeof window === 'undefined' || !window.gtag) return;

  if (analyticsConfig.enableDebugMode) {
    console.log('📄 Page View:', { url, title });
  }

  try {
    const configParams: Record<string, string | number | boolean | object> = {
      page_location: url,
    };

    if (title) {
      configParams.page_title = title;
    }

    window.gtag('config', analyticsConfig.measurementId, configParams);
  } catch (error) {
    if (analyticsConfig.enableDebugMode) {
      console.error('Page view tracking error:', error);
    }
  }
};

export const trackNavigation = (
  action: 'click' | 'menu_toggle' | 'logo_click' | 'back_navigation',
  label: string,
  destination?: string,
  currentPage?: string
): void => {
  trackEvent({
    category: 'navigation',
    action,
    label,
    custom_parameters: {
      destination,
      current_page: currentPage,
      device_type: getDeviceType(),
    },
  });
};

export const trackProject = (
  action: 'view_card' | 'click_card' | 'view_page' | 'external_link_click' | 'image_interaction' | 'gallery_interaction',
  projectData: {
    id: string;
    title: string;
    category?: string;
    year?: string;
    featured?: boolean;
  },
  additionalData?: {
    interactionType?: string;
    imageIndex?: number;
    externalUrl?: string;
  }
): void => {
  trackEvent({
    category: 'project',
    action,
    label: projectData.title,
    custom_parameters: {
      project_id: projectData.id,
      project_title: projectData.title,
      project_category: projectData.category,
      project_year: projectData.year,
      is_featured: projectData.featured,
      ...additionalData,
    },
  });
};

export const trackEngagement = (
  action: 'scroll_depth' | 'section_view' | 'time_on_page' | 'page_complete',
  label: string,
  data?: {
    scrollPercentage?: number;
    sectionName?: string;
    timeSpent?: number;
    pageUrl?: string;
  }
): void => {
  trackEvent({
    category: 'engagement',
    action,
    label,
    custom_parameters: {
      ...data,
      device_type: getDeviceType(),
    },
  });
};

export const trackContact = (
  action: 'resume_download' | 'email_click' | 'social_link_click' | 'contact_section_view',
  label: string,
  data?: {
    linkType?: 'resume' | 'email' | 'social';
    socialPlatform?: string;
    downloadType?: 'pdf';
  }
): void => {
  trackEvent({
    category: 'contact',
    action,
    label,
    custom_parameters: data as Record<string, string | number | boolean>,
  });
};

export const trackPerformance = (
  action: 'page_load' | 'interaction_delay' | 'animation_complete' | 'error',
  label: string,
  data?: {
    loadTime?: number;
    delayTime?: number;
    animationType?: string;
    errorType?: string;
    errorMessage?: string;
  }
): void => {
  if (!analyticsConfig.enablePerformanceTracking) return;

  trackEvent({
    category: 'performance',
    action,
    label,
    custom_parameters: data as Record<string, string | number | boolean>,
  });
};

export const getDeviceType = (): 'mobile' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop';
  return window.innerWidth <= 768 ? 'mobile' : 'desktop';
};

export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

export const getCurrentPagePath = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
};

export const getPageTitle = (): string => {
  if (typeof window === 'undefined') return '';
  return document.title;
};